import React, { useEffect, useState, useMemo } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { http } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2, History as HistoryIcon, TrendingUp } from "lucide-react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

export default function History() {
  const { user, login } = useAuth();
  const [items, setItems] = useState(null);

  useEffect(() => {
    if (!user) return;
    http.get("/assessment/history").then(({ data }) => setItems(data)).catch(() => setItems([]));
  }, [user]);

  const chartData = useMemo(() => {
    if (!items) return [];
    return items
      .map((a) => ({
        date: new Date(a.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
        raw: new Date(a.created_at).getTime(),
        score: a.report?.posture_score ?? a.vision?.posture_score ?? null,
        pattern: a.pattern?.name || "—",
      }))
      .filter((d) => d.score !== null)
      .sort((a, b) => a.raw - b.raw);
  }, [items]);

  const latest = chartData.length ? chartData[chartData.length - 1].score : null;
  const first = chartData.length ? chartData[0].score : null;
  const delta = latest !== null && first !== null ? latest - first : 0;

  if (!user) {
    return (
      <div className="text-center py-24">
        <HistoryIcon className="w-8 h-8 text-forest mx-auto mb-4" />
        <p className="mb-4">Sign in to view your assessment history.</p>
        <Button onClick={login} className="bg-forest hover:bg-forest/90 text-bone" data-testid="history-login">
          Sign in with Google
        </Button>
      </div>
    );
  }
  if (items === null) return <div className="text-center py-24"><Loader2 className="w-8 h-8 animate-spin text-forest mx-auto" /></div>;
  if (items.length === 0) return <div className="text-center py-24 text-muted-foreground">No saved assessments yet.</div>;

  return (
    <div className="animate-fade-in">
      <h1 className="font-heading text-3xl font-semibold text-forest mb-2">Your Progress</h1>
      <p className="text-muted-foreground mb-6">Every saved assessment plotted over time.</p>

      {chartData.length >= 2 && (
        <Card className="p-6 bg-white border-forest/10 mb-8" data-testid="progress-chart">
          <div className="flex items-start justify-between mb-4">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1">Posture Index trend</p>
              <p className="font-heading text-3xl font-semibold text-forest">{latest}<span className="text-lg text-muted-foreground">/100</span></p>
            </div>
            <Badge className={`${delta >= 0 ? "bg-sage/15 text-sage" : "bg-terracotta/15 text-terracotta"} border-0 gap-1`}>
              <TrendingUp className={`w-3 h-3 ${delta < 0 ? "rotate-180" : ""}`} />
              {delta >= 0 ? "+" : ""}{delta} pts
            </Badge>
          </div>
          <div className="w-full h-64">
            <ResponsiveContainer>
              <AreaChart data={chartData} margin={{ top: 8, right: 12, left: -12, bottom: 0 }}>
                <defs>
                  <linearGradient id="scoreFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#1B4332" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#1B4332" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#E5E7EB" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="date" stroke="#6B7280" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis domain={[0, 100]} stroke="#6B7280" fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip
                  contentStyle={{ background: "#FFF", border: "1px solid #E5E7EB", borderRadius: 8, fontSize: 12 }}
                  formatter={(v, _n, p) => [`${v}/100 · ${p.payload.pattern}`, "Score"]}
                />
                <Area type="monotone" dataKey="score" stroke="#1B4332" strokeWidth={2.5} fill="url(#scoreFill)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}

      <h2 className="font-heading text-xl font-semibold text-forest mb-4">Saved assessments</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((a) => (
          <Card key={a.assessment_id} className="p-5 bg-white border-forest/10">
            <p className="text-xs text-muted-foreground">{new Date(a.created_at).toLocaleString()}</p>
            <p className="font-heading text-lg font-semibold text-forest mt-1">
              {a.pattern?.name || "Assessment"}
            </p>
            {a.report?.posture_score && (
              <Badge className="mt-2 bg-honey text-forest border-0">Score {a.report.posture_score}</Badge>
            )}
            {a.report?.summary && <p className="text-sm mt-2 text-ink/80">{a.report.summary}</p>}
          </Card>
        ))}
      </div>
    </div>
  );
}
